/*! ════════════════════════════════════════════════════════════════
 *  © 2026 Sick Aura Inc. All rights reserved.
 *  HOLO KICK — the bicycle-kick product-page mark as a REAL hologram.
 *  three.js fullscreen-quad shader over the SVG mask: the red fill
 *  becomes retroreflective foil morphing through the SAME spectrum as
 *  the Hologram Reflective Glow ball (foil-ball's cosine palette,
 *  dye-deepened), with drifting interference bands, glitter sparkle
 *  and a periodic specular sweep. Black stroke stays black. Pointer
 *  tilt slides the spectrum like tilting a holo sticker.
 *  ════════════════════════════════════════════════════════════════ */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';

document.querySelectorAll('canvas[data-holo-kick]').forEach(initHoloKick);

function initHoloKick(canvas){
  const img=new Image();
  img.crossOrigin='anonymous';
  img.onload=()=>{ start(); };
  img.onerror=()=>{};                                    // img fallback stays visible
  img.src=canvas.dataset.holoKick;

  function start(){
    const S=1024, cv=document.createElement('canvas'); cv.width=cv.height=S;
    cv.getContext('2d').drawImage(img,0,0,S,S);
    const mask=new THREE.CanvasTexture(cv);
    mask.colorSpace=THREE.SRGBColorSpace;

    let renderer;
    try{ renderer=new THREE.WebGLRenderer({canvas, antialias:true, alpha:true}); }
    catch(e){ return; }                                  // no WebGL: keep the <img>
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio||1));
    renderer.setClearColor(0x000000,0);

    const scene=new THREE.Scene();
    const camera=new THREE.OrthographicCamera(-1,1,1,-1,0,1);
    const uni={
      uMask:{value:mask},
      uTime:{value:0},
      uTilt:{value:new THREE.Vector2(0,0)},
    };
    scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2,2), new THREE.ShaderMaterial({
      transparent:true,
      uniforms:uni,
      vertexShader:'varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position.xy,0.0,1.0); }',
      fragmentShader:`
        uniform sampler2D uMask; uniform float uTime; uniform vec2 uTilt;
        varying vec2 vUv;
        vec3 holo(float t){ return 0.5+0.5*cos(6.28318*(0.5*t+vec3(0.82,0.47,0.17))); }  // foil-ball palette
        float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
        void main(){
          vec4 m=texture2D(uMask, vUv);
          if(m.a<0.03) discard;
          vec3 col;
          if(m.r>0.35){                                   // the red fill -> holographic foil
            // interference bands: position + slow morph + tilt slide, like turning a sticker
            float wave=0.20*sin(vUv.x*9.0 - uTime*0.55) + 0.14*sin(vUv.y*13.0 + uTime*0.38);
            float t=1.15*vUv.y + 0.75*vUv.x + wave
                  + 0.12*uTime
                  + 1.4*uTilt.x*(vUv.y-0.5) + 1.4*uTilt.y*(vUv.x-0.5);
            vec3 rainbow=pow(holo(t), vec3(2.1))*2.2;     // dye-DEEP, exactly the ball's grade
            // glitter flakes twinkling in and out
            float g=hash(floor(vUv*150.0)+floor(uTime*2.0));
            rainbow+=vec3(1.0)*step(0.993,g)*0.85;
            // periodic specular sweep across the figure
            float ph=fract(uTime*0.11);
            float d=abs((vUv.x*0.7+vUv.y*0.5) - (ph*2.0-0.35));
            rainbow+=vec3(1.0)*smoothstep(0.10,0.0,d)*0.55;
            col=rainbow;
          } else {
            col=vec3(0.03);                               // the stroke stays ink-black
          }
          float a=m.a;
          gl_FragColor=vec4(col*a, a);                    // premultiplied
        }`
    })));

    function resize(){
      const w=canvas.clientWidth||1, h=canvas.clientHeight||1;
      renderer.setSize(w,h,false);
    }
    new ResizeObserver(resize).observe(canvas); resize();

    /* pointer tilt — hovering slides the whole spectrum */
    const tiltT=new THREE.Vector2(0,0);
    canvas.parentElement.addEventListener('pointermove', e=>{
      const r=canvas.getBoundingClientRect();
      tiltT.set(((e.clientX-r.left)/r.width-0.5)*2, ((e.clientY-r.top)/r.height-0.5)*2);
    });
    canvas.parentElement.addEventListener('pointerleave', ()=>tiltT.set(0,0));

    let running=true;
    if('IntersectionObserver' in window){
      new IntersectionObserver(es=>es.forEach(e=>{ running=e.isIntersecting; }),{threshold:0})
        .observe(canvas);
    }
    const clock=new THREE.Clock();
    (function frame(){
      requestAnimationFrame(frame);
      const dt=Math.min(clock.getDelta(),0.05);
      if(!running||document.hidden) return;
      uni.uTime.value+=dt;
      uni.uTilt.value.lerp(tiltT,Math.min(1,dt*5));
      renderer.render(scene,camera);
    })();

    canvas.classList.add('is-holo');                     // reveals canvas, hides the fallback img
  }
}
